import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Link,
  NavigateFunction,
  useNavigate,
  useParams,
} from 'react-router-dom'

import {
  PLAYLIST_THEMES_LIMIT_AMOUNT,
  PLAYLIST_THEMES_UNLIMITED,
} from '@disco/constants/FeatureTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import * as ShortlistActions from '@disco/state-management/actions/ShortlistActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { getFeatureData } from '@disco/state-management/selectors/business'
import { getShortlistById } from '@disco/state-management/selectors/shortlists'
import type { TrialLeft } from '@disco/utils/trial'

import type { PlaylistTheme } from 'types/playlist'
import type { Shortlist } from 'types/shortlist'

import ThemeExplorer from 'components/theme-explorer'
import Alert from 'components/ui/alert'
import Button from 'components/ui/button'
import Heading from 'components/ui/heading'

import type { RootState } from 'store/configureStore'
import styles from './index.module.css'

function mapStateToProps(state: RootState, ownProps: { shortlistId: string }) {
  const { isTrial, trialLeft, isFeatureEnabled } = getFeatureData(state, {
    featureName: PLAYLIST_THEMES_UNLIMITED,
  })
  return {
    ...state.themes,
    shortlist: getShortlistById(state, {
      id: ownProps.shortlistId,
    }),
    isTrial,
    trialLeft,
    themesLimit: !isFeatureEnabled,
    themesLoaded: state.themes.isFetched,
    isPhone: isDeviceTypePhone(state),
  }
}

const mapDispatchToProps = { ...ShortlistActions, sendMixpanelEvent }

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type from Redux
type PropsFromRedux = ConnectedProps<typeof connector>

// Own props interface
interface OwnProps {
  shortlistId: string
  navigate: NavigateFunction
}

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps

class ShortlistThemeSelect extends React.Component<
  Props,
  {
    isCreating: boolean
  }
> {
  state = {
    isCreating: false,
  }
  _closeModal = () => this.props.navigate('/')

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (!nextProps.shortlist) {
      this.props.navigate('/')
    }
  }

  _onThemeSave = (theme: PlaylistTheme | null) => {
    this.props.sendMixpanelEvent('Default playlist - Save theme')
    this.props.updateShortlist(this.props.shortlistId, {
      theme: theme,
    })
  }
  _onThemeSelect = (theme: PlaylistTheme | null) => {
    this.props.sendMixpanelEvent('Default playlist - Attach theme')
    this.props.updateShortlist(this.props.shortlistId, {
      theme: theme,
    })
  }
  _handleCreateTheme = () => {
    this.props.sendMixpanelEvent('Default playlist - Create new theme')
    this.setState({
      isCreating: true,
    })
  }
  _handleCloseCreateTheme = () => {
    this.setState({
      isCreating: false,
    })
  }

  render() {
    const {
      shortlist,
      isTrial,
      trialLeft,
      themes,
      themesLimit,
      themesLoaded,
      isPhone,
    } = this.props
    if (!shortlist) return null
    const actionDisabled = themesLimit && themes.length > 2
    return (
      <>
        <Heading size={500}>
          <div className={styles.shortlistThemeSelectHeading}>
            Playlist Themes{' '}
            {!isPhone && (
              <Button
                appearance="icon"
                icon="add-circle"
                tooltip="Create New Theme"
                onClick={this._handleCreateTheme}
                disabled={actionDisabled || !themesLoaded}
              />
            )}
          </div>
        </Heading>
        <ThemeExplorer
          isCreating={this.state.isCreating}
          onCancelCreating={this._handleCloseCreateTheme}
          onSave={this._onThemeSave}
          onSelect={this._onThemeSelect}
          activeTheme={this.props.shortlist.theme}
        />
        {themesLimit && (
          <div className={styles.shortlistThemeSelectTrial}>
            <Alert>
              {isTrial ? (
                <>
                  Heads up! {trialLeft.message}
                  <br />
                  Plus and Pro plans allow unlimited themes. The Lite plan is
                  limited to {PLAYLIST_THEMES_LIMIT_AMOUNT}.
                  <br />
                  Check out your plan options&nbsp;
                  <Link to="/settings/billing">here</Link>
                </>
              ) : (
                <>
                  Want more than {PLAYLIST_THEMES_LIMIT_AMOUNT} themes?{' '}
                  <Link to="/settings/billing">Upgrade</Link>
                </>
              )}
            </Alert>
          </div>
        )}
      </>
    )
  }
}

const ConnectedShortlistThemeSelect = connector(ShortlistThemeSelect)

function ShortlistThemeSelectWithHooks(props: any) {
  const params = useParams<{ shortlistId: string }>()
  const navigate = useNavigate()
  const shortlistId = params.shortlistId!

  return (
    <ConnectedShortlistThemeSelect
      {...props}
      shortlistId={shortlistId}
      navigate={navigate}
    />
  )
}

export default ShortlistThemeSelectWithHooks
export { ConnectedShortlistThemeSelect }
